/**
 * Expanded detail for a session.
 */
import * as React from 'react';
import { Calendar, Users, Ban } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { CancelSessionModal } from './CancelSessionModal';
import { VisioPopupBlockedModal } from '@/components/common/modals/VisioPopupBlockedModal';
import { cancelSession } from '@/services/inperson/sessions.service';
import { useJoinRemoteMeeting } from '@/hooks/useJoinRemoteMeeting';
import { usePageActions } from '@/hooks/usePageActions';
import { useSessionLiveKitPresence } from '@/hooks/useSessionsRemote';
import {
  SESSION_GROUP_STATUS_CONFIG,
  SESSION_DETAIL_TAB_CONFIGS,
  type SessionDetailTab,
  SESSION_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type {
  SessionWithParticipants,
  SessionGroup,
  SessionGroupChild,
  CancelSessionReasonCode,
} from '@/types/inperson/session.types';
import {
  formatSessionGroupCountLabel,
  getChildCancelTooltip,
  getInPersonStatusConfig,
  isSessionGroup,
  parseSessionGroupDate,
  tutorLabelOf,
} from './session-table-utils';
import { SessionDetailsTab } from './session-detail-tabs/SessionDetailsTab';
import { SessionParticipantsTab } from './session-detail-tabs/SessionParticipantsTab';
import { SessionReportsTab } from './session-detail-tabs/SessionReportsTab';
import { SessionHistoryTab } from './session-detail-tabs/SessionHistoryTab';
import {
  TutoringSessionContent,
  SessionInfoCards,
  SessionNotesCard,
  SessionParticipantsContent,
  SessionDetailFooter,
} from './session-detail-tabs/TutoringSessionContent';

/** Information-meeting detail: tabs + LiveKit presence on the participants tab. */
const InformationMeetingContent: React.FC<{ session: SessionWithParticipants }> = ({ session }) => {
  const [activeTab, setActiveTab] = React.useState<SessionDetailTab>('details');
  const { isJoining, join, popupBlocked } = useJoinRemoteMeeting();
  const { canJoinInformationMeeting } = usePageActions('sessions');
  const typeConfig = SESSION_TYPE_CONFIG[session.type];
  const statusConfig = getInPersonStatusConfig(session.status, session.type);
  const { data: presenceResponse, isLoading: presenceLoading } = useSessionLiveKitPresence(
    session.id,
    { refetchInterval: activeTab === 'participants' ? 5000 : false },
  );
  const sessionDate = parseSessionGroupDate(String(session.sessionDate ?? ''));
  const dayName = format(sessionDate, 'EEEE', { locale: fr });
  const dateFormatted = format(sessionDate, 'd MMMM yyyy', { locale: fr });
  const DETAIL_TABS: TabItem<SessionDetailTab>[] = SESSION_DETAIL_TAB_CONFIGS.map(
    ({ id, label, icon: Icon }) => ({ id, label, icon: <Icon className="w-4 h-4" /> }),
  );

  function handleJoin(): void {
    void join(session.id);
  }

  return (
    <div className="space-y-6">
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Calendar className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)] capitalize">
                {dayName} {dateFormatted}
              </h3>
              <DetailBadge
                label={typeConfig?.label ?? session.type}
                icon={typeConfig?.icon}
                colorName={typeConfig?.colorName ?? 'color-2'}
                variant="filled"
              />
              <DetailBadge
                label={statusConfig.label}
                icon={statusConfig.icon}
                colorName={session.status === 'COMPLETED' ? 'success' : 'error'}
                variant="outlined"
              />
            </div>
            {session.topic && (
              <p className="text-base text-[var(--color-1-nuance-2)]">{session.topic}</p>
            )}
          </div>
        </div>
      </div>
      <VisioPopupBlockedModal {...popupBlocked} />
      <Tabs
        items={DETAIL_TABS}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as SessionDetailTab)}
      />
      {activeTab === 'details' && (
        <SessionDetailsTab
          session={session}
          canJoinInformationMeeting={canJoinInformationMeeting}
          isJoining={isJoining}
          onJoin={handleJoin}
        />
      )}
      {activeTab === 'participants' && (
        <SessionParticipantsTab
          session={session}
          presenceResponse={presenceResponse}
          presenceLoading={presenceLoading}
        />
      )}
      {activeTab === 'reports' && <SessionReportsTab session={session} />}
      {activeTab === 'history' && <SessionHistoryTab session={session} />}
    </div>
  );
};

/** Cancel mutation for one group child — invalidates groups, sessions, and VSC reports. */
const useChildCancel = (childId: string) => {
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const cancelMutation = useMutation({
    mutationFn: ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail?: string }) =>
      cancelSession(childId, { reasonCode, reasonDetail }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['session-groups'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['session-group'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['sessions'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      setCancelOpen(false);
      toast.success('Séance annulée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
  return { cancelOpen, setCancelOpen, cancelMutation };
};

/** Cancel button for a group child — disabled once COMPLETED / CLOSED / CANCELLED. */
const CancelChildButton: React.FC<{ child: SessionGroupChild; index: number }> = ({
  child,
  index,
}) => {
  const { cancelOpen, setCancelOpen, cancelMutation } = useChildCancel(child.id);
  const isCancelled = child.status === 'CANCELLED';
  const isTerminated = isCancelled || child.status === 'COMPLETED' || child.status === 'CLOSED';
  const label = tutorLabelOf(child);

  return (
    <>
      <Button
        variant="ghost"
        color="rgb(var(--color-error))"
        size="sm"
        icon={Ban}
        disabled={isTerminated}
        onClick={() => setCancelOpen(true)}
        tooltipText={getChildCancelTooltip(isCancelled, isTerminated)}
      >
        Annuler cette séance
      </Button>
      <CancelSessionModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={(reasonCode: CancelSessionReasonCode, reasonDetail?: string) =>
          cancelMutation.mutate({ reasonCode, reasonDetail })
        }
        itemName={label ? `Séance de ${label}` : `Séance #${index + 1}`}
        isLoading={cancelMutation.isPending}
      />
    </>
  );
};

/** One child session card inside a group detail (status + tutor + cancel). */
const GroupChildCard: React.FC<{ child: SessionGroupChild; index: number }> = ({
  child,
  index,
}) => {
  const statusConfig =
    SESSION_GROUP_STATUS_CONFIG[child.status as keyof typeof SESSION_GROUP_STATUS_CONFIG] ??
    SESSION_GROUP_STATUS_CONFIG.PLANNED;
  const label = tutorLabelOf(child);
  const isCancelled = child.status === 'CANCELLED';

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        borderColor: 'color-mix(in srgb, var(--foreground) 12%, transparent)',
        opacity: isCancelled ? 0.7 : 1,
      }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[color-mix(in_srgb,var(--foreground)_3%,transparent)] border-b border-[color-mix(in_srgb,var(--foreground)_8%,transparent)]">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shrink-0"
            style={{
              background: 'color-mix(in srgb, var(--color-1-nuance-1) 12%, transparent)',
              color: 'var(--color-1-nuance-1)',
            }}
          >
            G{index + 1}
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border shrink-0"
            style={{
              color: statusConfig.badgeColor,
              borderColor: statusConfig.badgeColor,
              background: `color-mix(in srgb, ${statusConfig.badgeColor} 12%, transparent)`,
            }}
          >
            {statusConfig.label}
          </span>
          {label && <span className="text-sm font-medium text-foreground truncate">{label}</span>}
        </div>
        <div className="shrink-0">
          <CancelChildButton child={child} index={index} />
        </div>
      </div>
      <div className="px-4 py-4">
        <SessionParticipantsContent
          session={child as unknown as SessionWithParticipants}
          pairGridClassName="grid grid-cols-1 gap-3"
        />
      </div>
    </div>
  );
};

/** Parent-group detail: shared metadata plus one card per child. */
const SessionGroupDetail: React.FC<{ group: SessionGroup }> = ({ group }) => {
  const children = group.sessions ?? [];
  if (children.length === 1) {
    return <>{renderSessionDetails(children[0] as unknown as SessionWithParticipants)}</>;
  }

  const typeConfig = SESSION_TYPE_CONFIG[group.type];
  const statusConfig =
    SESSION_GROUP_STATUS_CONFIG[group.status] ?? SESSION_GROUP_STATUS_CONFIG.PLANNED;
  const groupDate = parseSessionGroupDate(group.sessionDate ?? '');
  const cancelledCount = children.filter((c) => c.status === 'CANCELLED').length;
  const activeCount = children.length - cancelledCount;
  const sharedSession = children[0] as unknown as SessionWithParticipants;

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Calendar className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)] capitalize">
                {format(groupDate, 'EEEE d MMMM yyyy', { locale: fr })}
              </h3>
              <DetailBadge
                label={typeConfig?.label ?? group.type}
                icon={typeConfig?.icon}
                colorName={typeConfig?.colorName ?? 'color-2'}
                variant="filled"
              />
              <DetailBadge
                label={statusConfig.label}
                icon={statusConfig.icon}
                colorName={group.status === 'CANCELLED' ? 'error' : 'success'}
                variant="outlined"
              />
              <DetailBadge
                label={formatSessionGroupCountLabel(children.length, activeCount, cancelledCount)}
                icon={Users}
                colorName="color-1"
                variant="outlined"
              />
            </div>
            {group.establishmentName && (
              <p className="text-base text-[var(--color-1-nuance-2)]">{group.establishmentName}</p>
            )}
          </div>
        </div>
      </div>
      <div className="details-view-grid">
        <SessionInfoCards session={sharedSession} />
        <DetailsCard
          icon={Users}
          title={`Répartition · ${children.length} groupe${children.length > 1 ? 's' : ''}`}
          colorName="color-3"
          colSpan="full"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {children.map((child, index) => {
              const isLoneLast = children.length % 2 === 1 && index === children.length - 1;
              return (
                <div
                  key={child.id}
                  className={isLoneLast ? 'md:col-span-2 md:w-1/2 md:justify-self-center' : ''}
                >
                  <GroupChildCard child={child} index={index} />
                </div>
              );
            })}
          </div>
        </DetailsCard>
        <SessionNotesCard session={sharedSession} />
      </div>
      <SessionDetailFooter session={sharedSession} />
    </>
  );
};

/** Detail renderer: group vs information-meeting vs tutoring. */
export const renderSessionDetails = (
  data: SessionWithParticipants | SessionGroup,
): React.ReactNode => {
  if (isSessionGroup(data)) {
    return <SessionGroupDetail group={data} />;
  }
  if (data.type === 'information_meeting_in_person') {
    return <InformationMeetingContent session={data} />;
  }
  return <TutoringSessionContent session={data} />;
};
